/* ---------------------------------------------------------------------------
   The address bar, and the state it names.

   Screen state used to live only in memory, so a reload came back to the
   overview and nothing could be sent to anyone: not a record, not a filtered
   register, not a map view. This module makes the URL the name of what is on
   screen, in both directions — encode() writes state into the hash, decode()
   reads a hash back into a state patch, and startRouter() wires the two to the
   store and to the browser's own back and forward.

   **Hash routing, deliberately.** Every route is one document, so there is no
   Pages `_redirects`, no SPA fallback and no dev-server rewrite to keep in step
   with each other. The cost is a `#` in every link; the saving is that a route
   cannot 404 in one environment and work in another.

   **The path is history; the query is the view of it.** Moving between screens,
   or opening a different structure, pushes an entry — those are the steps a
   person means to be able to walk back. Typing in the search box, dragging the
   map or clicking a column heading only replaces the current entry: a pan that
   pushed would bury the previous screen under two hundred entries of its own.

   **Dialogs are not in the URL at all.** They are transient state over a screen,
   not a place, and an "Add inspection" dialog that pushed an entry would make
   Back mean "close the dialog" on one press and "leave the record" on the next.
   Nothing here reads a dialog field, so opening one produces no address change.

   One thing the URL cannot settle on its own: a link naming a structure the
   bundled seed does not carry. The register hydrates from D1 a moment after
   boot, so the id in a shared link is routinely unknown at the instant it is
   read and known by the time the first request lands. It is therefore taken on
   trust and checked once, after hydrate has answered — see settlePending.
   --------------------------------------------------------------------------- */

import { actions, store, type MapView, type State } from './store.ts';
import { APPS, EXPLORER_PAGES, appById, isAppId, owns, setRunningApp, type AppDef, type AppId } from './apps.ts';
import { deskSendsHome, shellScreen } from './shell.ts';
import type { Shell } from '../data/types.ts';
import { getRegister } from '../data/register.ts';
import { ALL_DISTRICTS, ALL_ROADS, SORT_KEYS } from '../screens/register.ts';
import { defaultGroupBy, groupBys } from '../data/summary.ts';
import type { DetailView, MapBase, MapMode } from '../data/types.ts';
import { SCREENS as RATING_SCREENS, type Screen as RatingScreen } from '../rating/store.ts';
import { followRatingAddress } from '../rating/mount.ts';
import { isIsoDay, isPeriodKind, periodFor, type PeriodKind } from '../data/periods.ts';

const MAP_BASES: readonly MapBase[] = ['street', 'topo'];
const MAP_MODES: readonly MapMode[] = ['2d', '3d', 'fly'];

/* --------------------------- encoding --------------------------- */

/**
 * The property-panel page an address names, as `?page=` (LC-31): the explorer
 * entry picked beside the 3D model or the programme. Bridge info is the
 * default and is not written. Words rather than the store's names where the two
 * differ, since this is what gets pasted.
 */
const PAGE_SLUG: Readonly<Partial<Record<DetailView, string>>> = {
  state: 'state',
  inspections: 'inspections',
  condition: 'condition',
  defects: 'defects',
  changes: 'changes',
  photos: 'photos',
  rating: 'form',
  ratingHistory: 'on-record',
  baseline: 'baseline',
  variance: 'variance',
};

/** Monitoring's site-record pages, which are the content panel and so the path: `#/site/<id>/charts`. */
const SITE_SLUG: Readonly<Partial<Record<DetailView, string>>> = {
  works: 'works',
  returns: 'returns',
  incidents: 'incidents',
  documents: 'documents',
  tests: 'tests',
  siteItems: 'holds',
  charts: 'charts',
  progressPhotos: 'photos',
};

function viewOf(slugs: Readonly<Partial<Record<DetailView, string>>>, raw: string | null | undefined): DetailView | null {
  if (!raw) return null;
  const found = (Object.keys(slugs) as DetailView[]).find((v) => slugs[v] === raw);
  return found ?? null;
}

/** The `?page=` of a state: its explorer page, where the app's explorer offers it. */
function pageParam(s: Readonly<State>, p: URLSearchParams): URLSearchParams {
  if (EXPLORER_PAGES[s.app].includes(s.detailView)) p.set('page', PAGE_SLUG[s.detailView] ?? s.detailView);
  return p;
}

/**
 * Bridge Rating's own screens (LC-31): the screen, the structure in hand, and
 * the rating open on the Ratings screen. `#/rating/ratings/PK-N5-1602` is the
 * rating of that bridge; `?rating=` names one on record.
 */
function ratingHash(s: Readonly<State>): string {
  const p = new URLSearchParams();
  if (s.ratingScreen === 'ratings' && s.ratingOpen) p.set('rating', s.ratingOpen);
  pageParam(s, p);
  const id = s.selectedId ? `/${encodeURIComponent(s.selectedId)}` : '';
  return withQuery(`#/rating/${s.ratingScreen}${id}`, p);
}

/**
 * Where the network map is looking, as one parameter: `lat,lng,zoom`.
 *
 * Five decimal places is about a metre, which is finer than any coordinate in
 * the register is actually known to; two on the zoom keeps a fractional fit
 * (see MapView) from rounding to a visibly different frame. Both are fixed
 * widths so that encode → decode → encode returns the same string — a format
 * that drifted by a digit would rewrite the address on every navigation.
 */
function formatView(v: MapView): string {
  return `${v.lat.toFixed(5)},${v.lng.toFixed(5)},${v.zoom.toFixed(2)}`;
}

function parseView(raw: string | null): MapView | null {
  if (!raw) return null;
  const parts = raw.split(',');
  if (parts.length !== 3) return null;
  if (parts.some((p) => p.trim() === '')) return null;
  const [lat, lng, zoom] = parts.map(Number) as [number, number, number];
  if (![lat, lng, zoom].every(Number.isFinite)) return null;
  // Web Mercator gives out past ±85°, and no tile source here serves past 24.
  if (Math.abs(lat) > 85 || Math.abs(lng) > 180 || zoom <= 0 || zoom > 24) return null;
  return { lat, lng, zoom };
}

/**
 * A path and its query, with commas left as commas.
 *
 * URLSearchParams percent-encodes them, which turns the map's centre into
 * `at=27.67891%2C84.43220%2C9.43` in something whose entire purpose is to be
 * pasted into a message. A comma is a legal sub-delimiter in a query string
 * (RFC 3986 §3.4) and URLSearchParams reads it back unchanged, so this is
 * presentation only.
 */
function withQuery(path: string, params: URLSearchParams): string {
  const qs = params.toString().replace(/%2C/g, ',');
  return qs ? `${path}?${qs}` : path;
}

/**
 * The address for a given state.
 *
 * Only what differs from the default is written, so `#/register` means the
 * whole register sorted by name and `#/map` means the home view of it. The one
 * exception is the selection, which is written in full on both of those screens
 * — the map highlights it and draws its footprint, the register highlights its
 * row and can show it in the preview pane, and a default that varies by whoever
 * opens the link is no default at all.
 */
export function encode(s: Readonly<State>): string {
  return withApp(s, s.app, s.screen, screenHash(s));
}

/**
 * THE APP IS THE FIRST SEGMENT WHERE THE SCREEN DOES NOT ALREADY SAY IT.
 *
 * One page carries all four applications now, and three of them draw the
 * register — so `#/register` alone no longer says whose register it is. It
 * still means what it always meant, Bridge Inspection's, and
 * `#/scheduler/register` is the Scheduler's. The rule is one line: an address
 * with no app in it means the first app that draws that screen (`ownerOf`), and
 * the app is written only when it is some other one. So every address this
 * build wrote before the apps shared a page still means the same screen in the
 * same app, and the common ones stay short: `#/bridge/…`, `#/schedule/…`,
 * `#/site/…`, `#/works`. Bridge Rating keeps its screens in its own store, and
 * its address names the screen and the structure after the app —
 * `#/rating/ratings/<id>` — from the copy this store keeps (LC-31); a bare
 * `#/rating`, all it had before, still opens it.
 */
function withApp(s: Readonly<State>, app: AppId, screen: State['screen'], hash: string): string {
  /* A separate app's own screens are its store's, and this store keeps the copy
     of them its address needs (LC-31) — see `ratingHash`. The 3D model, which
     this store draws for it, is addressed like any other screen. */
  if (appById(app).separate && !owns(appById(app), screen)) return ratingHash(s);
  /* A screen the app does not draw is named by the screen alone. The router
     never leaves the two like that (see routeFor), but a link built for a
     screen of another app — `hashFor` — is exactly this, and means the app
     that does draw it. */
  if (!owns(appById(app), screen) || app === ownerOf(screen)) return hash;
  const rest = hash.replace(/^#\/?/, '');
  return rest ? `#/${app}/${rest}` : `#/${app}`;
}

/** The app an address with no app segment in it meant: the first that draws its screen. */
function ownerOf(screen: State['screen']): AppId {
  return (APPS.find((a) => owns(a, screen)) ?? appById('inspection')).id;
}

/** The address of a screen within whichever app draws it, without the app. */
function screenHash(s: Readonly<State>): string {
  switch (s.screen) {
    case 'dashboard':
      // Network-wide, and reads nothing per-structure. Nothing to carry.
      return '#/';

    case 'options':
      /* Field-shell only, and it carries nothing. Every switch on it is a
         persisted preference or a fact about this device, so there is no view of
         it worth naming in a link — and a link that set somebody else's shell by
         opening it would be the `?shell=` override with none of its
         deliberateness. */
      return '#/options';

    case 'people':
      /* Administration, and it carries nothing worth naming. The form being
         typed into is a draft rather than a place — the same rule dialogs follow
         at the top of this file — and the list is whatever the register holds
         when it is read. A link to this address is a link to the screen, which is
         the useful thing to be able to send somebody. */
      return '#/people';

    case 'guide':
      /* One document, so there is nothing about it to carry. Which section you
         are reading is a scroll position and not state — see the note at the top
         of src/screens/guide.ts — and it deliberately stays that way, because the
         alternative is a fragment inside a fragment on an application that routes
         on the fragment. */
      return '#/guide';

    case 'works': {
      /* The period IS the document. A link to the September report that opened
         whatever month the reader happens to be in would be a different report
         under the same address, which is the one thing a link to a report must
         not be — so both ends travel, and the cadence with them so the heading
         and the previous/next controls behave as the sender's did. */
      const p = new URLSearchParams();
      p.set('period', s.worksPeriod);
      p.set('from', s.worksRange.from);
      p.set('to', s.worksRange.to);
      return withQuery('#/works', p);
    }

    case 'summary': {
      /* Also network-wide, but the grouping IS the report: a link to a
         division summary that opens a district one is a different document.
         Carried even though it is a persisted preference, for the same reason
         the map carries its mode. */
      const p = new URLSearchParams();
      if (s.summaryBy !== 'district') p.set('by', s.summaryBy);
      return withQuery('#/summary', p);
    }

    /* The page beside the model, where the explorer picked one (LC-31). */
    case 'detail':
      return withQuery(`#/bridge/${encodeURIComponent(s.selectedId)}`, pageParam(s, new URLSearchParams()));

    /* One structure's dashboard (LC-30), under the structure like its sheets. */
    case 'board':
      return `#/bridge/${encodeURIComponent(s.selectedId)}/dashboard`;

    /* The two other applications' structure screens, each naming its structure
       in the path for the same reason the record does: the bridge IS the
       subject, and a link to "the programme" that opened whichever structure
       the reader last had selected would be a link to a different document for
       every reader. Their own pages, so the same path under a different
       document is a different app — see src/core/apps.ts. */
    /* And which of its two views fills the page, the Gantt being the bare
       address, with the explorer's page beside it (LC-31). */
    case 'schedule': {
      const view = s.scheduleView === 'activities' ? '/activities' : '';
      return withQuery(
        `#/schedule/${encodeURIComponent(s.selectedId)}${view}`,
        pageParam(s, new URLSearchParams()),
      );
    }

    /* And the site-record page open in the content panel (LC-31). */
    case 'site': {
      const page = SITE_SLUG[s.detailView];
      return `#/site/${encodeURIComponent(s.selectedId)}${page ? `/${page}` : ''}`;
    }

    case 'report':
    case 'sheet':
      return `#/bridge/${encodeURIComponent(s.selectedId)}/${s.screen}`;

    case 'list': {
      const p = new URLSearchParams();
      if (s.q.trim()) p.set('q', s.q);
      if (s.district !== ALL_DISTRICTS) p.set('district', s.district);
      if (s.road !== ALL_ROADS) p.set('road', s.road);
      if (s.sortKey !== 'name') p.set('sort', s.sortKey);
      if (s.sortDir !== 'asc') p.set('dir', s.sortDir);
      p.set('sel', s.selectedId);
      return withQuery('#/register', p);
    }

    case 'network': {
      const p = new URLSearchParams();
      /* Base and mode are persisted preferences rather than view state (see
         Persisted in src/data/types.ts), and carrying them means a link can
         override the preference of whoever opens it. Carried anyway: a link
         that says "this structure on its ground" and opens the flat map is a
         broken link, and the override is exactly what clicking the control
         yourself would have done. */
      if (s.mapBase !== 'street') p.set('base', s.mapBase);
      if (s.mapMode !== '2d') p.set('mode', s.mapMode);
      if (s.mapView) p.set('at', formatView(s.mapView));
      p.set('sel', s.selectedId);
      return withQuery('#/map', p);
    }
  }
}

/* --------------------------- decoding --------------------------- */

function pick<T extends string>(raw: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.includes(raw as T) ? (raw as T) : fallback;
}

/**
 * The state a given address names.
 *
 * Total: anything unrecognised reads as the overview rather than throwing or
 * leaving the app on whatever was there before. The caller re-encodes the
 * result and rewrites the address if the two differ, so a half-remembered or
 * hand-edited URL corrects itself in place instead of persisting as a lie.
 *
 * Every value that has a fixed set of allowed forms is checked against it. The
 * two that are not are the search text and the district, which are filters over
 * whatever the register happens to hold: a district this build has never heard
 * of is honestly "no rows match", not an error.
 */
export function decode(hash: string): Partial<State> {
  /* An app segment first, if there is one. Addresses written before the four
     apps shared a page have none, and mean whichever app drew that screen then —
     which is the first that owns it, in APPS order: the register is Bridge
     Inspection's, the guide is the start page's. */
  const bare = hash.replace(/^#\/?/, '');
  const first = bare.split(/[/?]/, 1)[0];
  if (first === 'rating') {
    const own = decodeRating(bare.slice(first.length).replace(/^\//, ''));
    if (own !== null) return { ...own, app: 'rating' };
  }
  if (isAppId(first) && first !== 'home') {
    const patch = decodeScreen(`#/${bare.slice(first.length).replace(/^\//, '')}`);
    return { ...patch, app: first };
  }
  const patch = decodeScreen(hash);
  return { ...patch, app: ownerOf(patch.screen ?? 'dashboard') };
}

/**
 * Bridge Rating's own screens, from what follows `#/rating/` (LC-31) — or null
 * where that is not one of them: `#/rating/bridge/…` is the 3D model. A bare
 * `#/rating`, the only address this app had before, is its screens as they
 * stand.
 */
function decodeRating(rest: string): Partial<State> | null {
  const cut = rest.indexOf('?');
  const path = cut === -1 ? rest : rest.slice(0, cut);
  const p = new URLSearchParams(cut === -1 ? '' : rest.slice(cut + 1));
  let segs: string[];
  try {
    segs = path.split('/').filter(Boolean).map(decodeURIComponent);
  } catch {
    return { screen: 'dashboard' };
  }
  if (segs.length === 0) return { screen: 'dashboard' };
  if (!RATING_SCREENS.includes(segs[0] as RatingScreen)) return null;
  const ratingScreen = segs[0] as RatingScreen;
  const out: Partial<State> = {
    screen: 'dashboard',
    ratingScreen,
    ratingOpen: ratingScreen === 'ratings' ? p.get('rating') || null : null,
    detailView: pageOf(p),
  };
  if (segs[1]) out.selectedId = segs[1];
  return out;
}

/** The explorer page an address names, Bridge info where it names none. */
function pageOf(p: URLSearchParams): DetailView {
  const view = viewOf(PAGE_SLUG, p.get('page')) ?? 'inventory';
  /* Photos was a page of its own; it is the Defects page's now. */
  return view === 'photos' ? 'defects' : view;
}

function decodeScreen(hash: string): Partial<State> {
  const raw = hash.replace(/^#/, '');
  const cut = raw.indexOf('?');
  const pathPart = cut === -1 ? raw : raw.slice(0, cut);
  const queryPart = cut === -1 ? '' : raw.slice(cut + 1);

  let segs: string[];
  try {
    segs = pathPart.split('/').filter(Boolean).map(decodeURIComponent);
  } catch {
    // A stray '%' makes decodeURIComponent throw. Not a route.
    return { screen: 'dashboard' };
  }
  const p = new URLSearchParams(queryPart);

  /* The record and its printable sheet are the two screens whose subject IS the
     structure, so the id sits in the path rather than the query. An unknown
     third segment reads as the record — `/report` is the only thing that means
     anything there. */
  if (segs[0] === 'bridge' && segs[1]) {
    if (segs[2] === 'report' || segs[2] === 'sheet') return { screen: segs[2], selectedId: segs[1] };
    if (segs[2] === 'dashboard') return { screen: 'board', selectedId: segs[1] };
    return { screen: 'detail', selectedId: segs[1], detailView: pageOf(p) };
  }

  /* An unknown view is the Gantt, as an unknown page is the site record's
     landing: the structure is what the address is sure of. */
  if (segs[0] === 'schedule' && segs[1]) {
    return {
      screen: 'schedule',
      selectedId: segs[1],
      scheduleView: segs[2] === 'activities' ? 'activities' : 'gantt',
      detailView: pageOf(p),
    };
  }

  if (segs[0] === 'site' && segs[1]) {
    return { screen: 'site', selectedId: segs[1], detailView: viewOf(SITE_SLUG, segs[2]) ?? 'inventory' };
  }

  if (segs[0] === 'register') {
    const out: Partial<State> = {
      screen: 'list',
      q: p.get('q') ?? '',
      district: p.get('district') ?? ALL_DISTRICTS,
      road: p.get('road') ?? ALL_ROADS,
      sortKey: pick(p.get('sort'), SORT_KEYS, 'name'),
      sortDir: p.get('dir') === 'desc' ? 'desc' : 'asc',
    };
    const sel = p.get('sel');
    if (sel) out.selectedId = sel;
    return out;
  }

  if (segs[0] === 'summary') {
    /* Read at parse time, not at module load: the tenant is resolved on boot
       and a link pasted into a DPWH session names a DPWH grouping. */
    return { screen: 'summary', summaryBy: pick(p.get('by'), groupBys(), defaultGroupBy()) };
  }

  if (segs[0] === 'works') {
    /* Both ends are validated as real calendar days before they are adopted,
       and a link carrying a half-written or impossible date falls back to the
       period the cadence names today rather than to an empty report somebody
       would read as "nothing was built". */
    const kind = isPeriodKind(p.get('period')) ? (p.get('period') as PeriodKind) : 'month';
    const from = p.get('from') ?? '';
    const to = p.get('to') ?? '';
    const range =
      isIsoDay(from) && isIsoDay(to) && from <= to
        ? { from, to }
        : periodFor(kind, new Date().toISOString().slice(0, 10));
    return { screen: 'works', worksPeriod: kind, worksRange: range };
  }

  if (segs[0] === 'options') {
    return { screen: 'options' };
  }

  if (segs[0] === 'guide') {
    return { screen: 'guide' };
  }

  /* Decoded for everybody, permitted for administrators. What an address MEANS
     does not depend on who is reading it — the same argument routeFor makes
     below about the shell — so the role is not consulted here. Somebody without
     it lands on the screen and the screen says no, which is a sentence they can
     read rather than a silent redirect to the overview. See
     src/screens/people.ts. */
  if (segs[0] === 'people') {
    return { screen: 'people' };
  }

  if (segs[0] === 'map') {
    const out: Partial<State> = {
      screen: 'network',
      mapBase: pick(p.get('base'), MAP_BASES, 'street'),
      mapMode: pick(p.get('mode'), MAP_MODES, '2d'),
      mapView: parseView(p.get('at')),
    };
    const sel = p.get('sel');
    if (sel) out.selectedId = sel;
    return out;
  }

  /* Deliberately not carrying the register's filters back to their defaults
     here. Leaving the register for the overview and returning to it should find
     the search still in force — it is the same visit. A bare `#/register`
     pasted from elsewhere does clear them, because that patch says so above. */
  return { screen: 'dashboard' };
}

/* --------------------------- the running router --------------------------- */

/** The address this module last put in the bar, so it can tell its own writes apart. */
let published = '';

/**
 * A structure named by an address that the register did not hold at the time.
 *
 * Almost always a real record: the bundled seed is the five drawing-verified
 * structures and D1 carries the rest, so any link to one of the others is
 * unknown for as long as the first request is in flight. Checked once, when
 * hydrate has said one way or the other — see settlePending.
 */
let pending: string | null = null;

let installed = false;

/** Everything before the `?` — the part of an address that is worth a history entry. */
function pathOf(hash: string): string {
  const cut = hash.indexOf('?');
  return cut === -1 ? hash : hash.slice(0, cut);
}

/**
 * Read an address into the store.
 *
 * The selection goes through the action rather than into the patch, because
 * moving to a different structure is more than one field: the model index and
 * defect log on screen belong to the structure being left, and have to be
 * cleared and re-fetched — see selectInto in src/core/store.ts. A link opened
 * cold would otherwise draw the previous structure's models against this one's
 * name.
 */
/**
 * What an address means to the shell that is reading it.
 *
 * `decode` stays a pure function of the address, because what a URL MEANS does
 * not depend on the device: `#/summary` names the network summary everywhere.
 * What differs is whether this shell will go there — the field shell reaches four
 * screens and an address naming one of the other three has to land somewhere
 * rather than draw a network report under a tab bar.
 *
 * THIS IS A SEPARATE, EXPORTED FUNCTION BECAUSE applyHash CANNOT BE ONE. That
 * function installs nothing and exports nothing, and it is reached only through
 * startRouter, which binds two window listeners and rewrites history — so a rule
 * written inline inside it is a rule no check can drive. It was written inline
 * first, and a mutation deleting it left the whole suite green: the check that
 * was supposed to cover it composed `decode` and `shellScreen` itself, in the
 * right order, and so tested its own arrangement rather than the router's. Fourth
 * time in this codebase that the fix for an unverifiable rule has been to move
 * it — see the 2026-08-19 entry in ROADMAP's Decisions.
 *
 * It also covers `decode`'s own fallback: an unrecognised address reads as the
 * overview, which is one of the three the field shell refuses.
 */
export function routeFor(hash: string, shell: Shell): Partial<State> {
  const patch = decode(hash);
  if (patch.screen) patch.screen = shellScreen(shell, patch.screen);

  /* The desk's register and network are its start page — see deskSendsHome.
     `#/register?district=Attock` keeps its filter; it is only drawn at home. */
  if (patch.screen && deskSendsHome(shell, patch.screen)) {
    patch.screen = 'dashboard';
    patch.app = 'home';
  }

  /* AND WHICH APP IS READING IT. The shell decides whether a device will draw a
     screen at all; this decides which app draws it. The field shell is one app
     whatever the address says (see `appForShell`). On the desk, the address
     names the app, and a screen that app does not have — `#/scheduler/summary`,
     a hand-edited link — opens that app's landing screen instead. Anything
     unreadable decodes to the start page, which is where a typo should land. */
  const app: AppDef = shell === 'field' ? appById('inspection') : appById(patch.app ?? 'home');
  patch.app = app.id;
  /* Bridge Rating's own screens are its store's, and this one marks them as
     `dashboard` — the address `#/rating/<screen>/<id>` — rather than sending them to its
     landing, which is the 3D model and would leave its screens unreachable. */
  if (patch.screen && !owns(app, patch.screen)) patch.screen = app.separate ? 'dashboard' : app.landing;
  return patch;
}

function applyHash(hash: string): void {
  const { selectedId, ...rest } = routeFor(hash, store.get().shell);
  const before = store.get();
  /* The running app and `State.app` move together — see `actions.openApp`. */
  if (rest.app) setRunningApp(rest.app);
  store.set(rest);
  /* And Bridge Rating's store is told what its part of the address says. */
  if (rest.app === 'rating' && rest.screen === 'dashboard' && rest.ratingScreen) {
    followRatingAddress(rest.ratingScreen, selectedId ?? before.selectedId, rest.ratingOpen ?? null);
  }
  if (rest.app && rest.app !== before.app) actions.appOpened();

  /* A SCREEN SHOWN BY AN ADDRESS IS STILL A SCREEN SHOWN. Reading an address is
     not navigating — `actions.go` is not involved and must not be, because it
     would push history for something that came out of history — but a screen
     that fetches when it is opened has to be told either way. Without this,
     reloading the page on `#/people` drew a People screen reporting that its own
     list had not been read: true, and useless. See `screenOpened`. */
  if (rest.screen && rest.screen !== before.screen) void actions.screenOpened(rest.screen);
  if (selectedId && selectedId !== store.get().selectedId) {
    pending = getRegister().some((b) => b.id === selectedId) ? null : selectedId;
    actions.selectBridge(selectedId);
  }

  /* Canonicalise in place. `#/register?dir=asc` and `#/nonsense` both name
     something this app can express more plainly, and an address left saying
     otherwise is one that gets copied. replaceState rather than push: correcting
     a URL is not a navigation, and pushing would make Back return to the
     malformed version. */
  const canonical = encode(store.get());
  published = canonical;
  if (canonical !== hash) window.history.replaceState(null, '', canonical);
}

/**
 * Settle a structure named by an address, now that the register is whatever it
 * is going to be.
 *
 * Unknown after hydrate means the link names nothing on file. The selection is
 * dropped rather than left standing, which is the rule the persisted selection
 * already follows (see load() in src/core/store.ts) and the only one that keeps
 * the address honest: findBridge falls back to the first record, so a selection
 * left pointing at nothing would draw some other structure's record under a URL
 * naming this one.
 */
function settlePending(): void {
  const id = pending;
  pending = null;
  if (!id) return;
  const register = getRegister();
  if (register.some((b) => b.id === id)) return;
  const fallback = register[0];
  if (fallback) actions.selectBridge(fallback.id);
}

/** Write the current state back to the address bar. Called on every store change. */
function publish(s: Readonly<State>): void {
  // 'unknown' means hydrate has not answered yet, either way.
  if (pending !== null && s.backend !== 'unknown') settlePending();

  // Re-read: settlePending may have moved the selection, and store.set applies
  // its patch synchronously even though it defers the repaint.
  const next = encode(store.get());
  if (next === published) return;
  const pushes = pathOf(next) !== pathOf(published);
  published = next;
  window.history[pushes ? 'pushState' : 'replaceState'](null, '', next);
}

/**
 * Point the app at the address it was opened on, and keep the two in step.
 *
 * Call before the first paint: this puts the URL's screen and selection into
 * the store, and painting first would show the overview for a frame before
 * replacing it with the record the link asked for.
 */
export function startRouter(): void {
  if (installed) return;
  installed = true;

  applyHash(window.location.hash);

  /* Both, because a step between two pushState entries with different fragments
     fires both events, and a plain fragment change fires only the second.
     Handling it twice costs nothing — the second pass decodes to the state
     already in force, so the store drops the patch and the address already
     matches.

     A back press can also belong to a framed modeller, which calls pushState of
     its own inside a same-origin iframe — the hero viewport and the import's
     hidden frame both run one. Those entries carry no fragment of ours, so this
     decodes the unchanged address, changes nothing, and leaves the press to the
     frame. */
  const onNavigate = (): void => applyHash(window.location.hash);
  window.addEventListener('hashchange', onNavigate);
  window.addEventListener('popstate', onNavigate);

  store.subscribe(publish);
}
